import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SESSION } from "@/config/session";
import { ChoiceStep, DetailsStep } from "./FormSteps";

const PROFILES = ["Salaried Professional", "Business Owner", "Self-employed Professional", "Homemaker", "Retired", "Student"];
const INCOMES = ["Below ₹50K", "₹50K – ₹1 Lakh", "₹1 Lakh – ₹2 Lakh", "₹2 Lakh+"];
const EMPTY = { profile: "", income: "", name: "", phone: "", email: "", city: "" };

export const RegisterModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const navigate = useNavigate();
  const onDone = (first) => {
    onClose();
    navigate(`/thank-you?name=${encodeURIComponent(first)}`);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  const pick = (key, next) => (v) => { setData((d) => ({ ...d, [key]: v })); setTimeout(() => setStep(next), 220); };
  const progress = ((step + 1) / 3) * 100;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/70 backdrop-blur-sm p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
          data-testid="register-modal-overlay"
        >
          <motion.div
            role="dialog" aria-modal="true" aria-labelledby="reg-title"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-7"
            data-lenis-prevent
            data-testid="register-modal"
          >
            <button type="button" onClick={onClose} aria-label="Close" data-testid="register-modal-close" className="absolute right-3 top-3 w-9 h-9 grid place-items-center rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <>
                <h2 id="reg-title" className="font-extrabold text-ink text-[19px] sm:text-[22px] leading-tight pr-8">Save Your Free Seat For The Wealth Code Masterclass</h2>
                <p className="text-[13px] sm:text-sm text-zinc-600 mt-1.5">Join Sandeep Gupta live on Zoom this {SESSION.shortLabel} at {SESSION.startTime}.</p>
                <div className="mt-4 h-2 rounded-full bg-zinc-100 overflow-hidden" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
                  <motion.div className="h-full cta-btn" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} data-testid="form-progress-bar" />
                </div>
                <p className="text-[12px] text-zinc-500 mt-1.5 font-medium">Step {step + 1} of 3</p>
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="mt-4">
                  {step === 0 && <ChoiceStep id="profile" title="What best describes you?" options={PROFILES} value={data.profile} onPick={pick("profile", 1)} />}
                  {step === 1 && <ChoiceStep id="income" title="What is your monthly income?" options={INCOMES} value={data.income} onPick={pick("income", 2)} />}
                  {step === 2 && <DetailsStep data={data} setData={setData} onDone={onDone} />}
                </motion.div>
                <div className="flex justify-between mt-5">
                  <button type="button" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="form-prev-button" className="px-5 py-2.5 rounded-xl border border-zinc-300 font-semibold text-sm text-ink disabled:opacity-40 hover:bg-zinc-50 transition-colors">Previous</button>
                  {step < 2 && (
                    <button type="button" disabled={step === 0 ? !data.profile : !data.income} onClick={() => setStep((s) => s + 1)} data-testid="form-next-button" className="px-6 py-2.5 rounded-xl bg-plum text-white font-semibold text-sm disabled:opacity-40 hover:bg-[#4a17b8] transition-colors">Next</button>
                  )}
                </div>
              </>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
